
import { useState, useEffect } from 'react';
import { useApplications } from "@/hooks/use-applications";
import { ApplicationStatus } from "./ApplicationStatus";
import { AIRecommendedProviders } from './AIRecommendedProviders';
import { ApplicationsList } from "./ApplicationsList";
import { Button } from "../ui/button";
import { FilePlus, CheckIcon, UnlinkIcon, Loader2, Building2, UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BrokerDashboard } from "../broker/BrokerDashboard";
import { BrokerLinkCard } from "../broker/BrokerLinkCard";
import { useBroker } from "@/hooks/use-broker";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from 'react-router-dom';

export const DashboardContent = () => {
  const { applications, isLoading, startNewApplication, refetch } = useApplications();
  const [isStartingNew, setIsStartingNew] = useState(false);
  const { userRole } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const { 
    linkedBroker, 
    isLoading: isBrokerLoading,
    unlinkBroker,
    isUnlinking 
  } = useBroker();
  
  // Check for submission flag and trigger refresh if needed
  useEffect(() => {
    const submissionFlag = sessionStorage.getItem('applicationSubmitted');
    
    if (submissionFlag) {
      console.log('Dashboard detected application submission, refreshing data');
      refetch().then(() => {
        console.log('Applications data refreshed after submission');
      });
    }
  }, [refetch]);
  
  const handleStartNewApplication = async () => {
    setIsStartingNew(true);
    try {
      await startNewApplication();
    } finally {
      setIsStartingNew(false);
    }
  };
  
  const handleUnlinkBroker = async () => {
    if (window.confirm('Are you sure you want to unlink from your broker? This action cannot be undone.')) {
      const success = await unlinkBroker();
      if (success) {
        toast({
          title: 'Successfully unlinked',
          description: 'You have been unlinked from your broker'
        });
      }
    }
  };
  
  const handleApplicationDeleted = async () => {
    try {
      await refetch();
    } catch (error) {
      console.error('Error refreshing applications:', error);
    }
  };
  
  if (userRole === 'broker') {
    return <BrokerDashboard />;
  }
  
  return (
    <div className="container mx-auto py-6 space-y-8 px-4 sm:px-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Dashboard</h2>
        <Button 
          onClick={handleStartNewApplication}
          disabled={isLoading || isStartingNew}
          className="flex items-center gap-2"
        >
          {isStartingNew ? (
            <>Creating...</>
          ) : (
            <>
              <FilePlus className="h-4 w-4" />
              New Application
            </>
          )}
        </Button>
      </div>
      
      {isBrokerLoading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <p>Loading broker information...</p>
            </div>
          </CardContent>
        </Card>
      ) : linkedBroker ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckIcon className="h-5 w-5 text-primary" />
              {linkedBroker.company_name ? (
                <>Connected to {linkedBroker.company_name}</>
              ) : (
                <>Linked to Broker</>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span>Broker ID: {linkedBroker.referral_code}</span>
                </div>
                {linkedBroker.broker_name && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <UserIcon className="h-4 w-4" />
                    <span>{linkedBroker.broker_name}</span>
                  </div>
                )}
                {linkedBroker.description && (
                  <p className="text-sm text-muted-foreground">
                    {linkedBroker.description}
                  </p>
                )}
                <p className="text-sm">
                  Your broker can assist with your PEO applications and provide expert guidance.
                </p>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleUnlinkBroker}
                  disabled={isUnlinking}
                >
                  {isUnlinking ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UnlinkIcon className="h-4 w-4 mr-2" />
                  )}
                  {isUnlinking ? "Unlinking..." : "Unlink Broker"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <BrokerLinkCard />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-bold mb-6">Application Status</h2>
          <ApplicationStatus />
        </div>
        
        <div>
          <h2 className="text-2xl font-bold mb-6">Your Applications</h2>
          <ApplicationsList 
            applications={applications} 
            isLoading={isLoading}
            onApplicationDeleted={handleApplicationDeleted}
          />
        </div>
      </div>
      
      {applications && applications.length > 0 && (
        <AIRecommendedProviders applicationId={applications[0].id} />
      )}
    </div>
  );
};
